import { Routes } from '@angular/router';

import { AdminComponent } from './components/admin/admin.component';
import { AppSettingsComponent } from './components/admin/app-settings/app-settings.component';
import { AuditHistoryComponent } from './components/admin/audit-history/audit-history.component';
import { FeaturesComponent } from './components/admin/feature-flags/feature-flags.component';
import { LogsComponent } from './components/admin/logs/logs.component';
import { OfficeDetailsComponent } from './components/admin/office-details/office-details.component';
import { OfficesComponent } from './components/admin/offices/offices.component';
import { PracticeAreaDetailsComponent } from './components/admin/practice-area-details/practice-area-details.component';
import { PracticeAreasComponent } from './components/admin/practice-areas/practice-areas.component';
import { RolesComponent } from './components/admin/roles/roles.component';
import { UserFeedbackComponent } from './components/admin/user-feedback/user-feedback.component';
import { UsersComponent } from './components/admin/users/users.component';
import { ClientsMattersComponent } from './components/clients-matters/clients-matters.component';
import { DashboardComponent } from './components/dashboard/dashboard.component';
import { LoginComponent } from './components/login/login.component';
import { MatterDetailsComponent } from './components/matters/matter-details.component';
import { RedirectComponent } from './components/redirect/redirect.component';
import { RequestDetailsComponent } from './components/requests/request-details.component';
import { RequestsComponent } from './components/requests/requests.component';
import { ResourceDetailsComponent } from './components/resources/resource-details.component';
import { ResourcesComponent } from './components/resources/resources.component';
import { UserProfileComponent } from './components/user-profile/user-profile.component';

import { AUTH_BRIDGE_ROUTE } from './config/auth-bridge.config';
import { authGuard } from './guards/auth.guard';
import {
  appSettingsManagementGuard,
  auditHistoryGuard,
  clientMattersGuard,
  logsGuard,
  permissionsManagementGuard,
  practiceAreasManagementGuard,
  requestsGuard,
  userFeedbackGuard,
  usersManagementGuard,
} from './guards/permission.guard';
import { superAdminGuard } from './guards/super-admin.guard';
import { UnsavedChangesGuard } from './guards/unsaved-changes.guard';

export const routes: Routes = [
  { path: '', component: LoginComponent },
  { path: 'login', component: LoginComponent },
  // MSAL redirect-bridge landing page — must stay unguarded (see RedirectComponent)
  { path: AUTH_BRIDGE_ROUTE, component: RedirectComponent },
  {
    path: 'user-profile',
    component: UserProfileComponent,
    canActivate: [authGuard],
    canDeactivate: [UnsavedChangesGuard],
    data: { breadcrumb: 'User Profile' },
  },
  {
    path: 'resources',
    component: ResourcesComponent,
    canActivate: [authGuard],
    data: { breadcrumb: 'Resources' },
  },
  {
    path: 'resources/:employeeId',
    component: ResourceDetailsComponent,
    canActivate: [authGuard],
    canDeactivate: [UnsavedChangesGuard],
    data: {
      breadcrumbPath: [
        { label: 'Resources', url: '/resources' },
        { label: ':employeeId', url: '' },
      ],
    },
  },
  {
    path: 'requests',
    component: RequestsComponent,
    canActivate: [authGuard, requestsGuard],
    data: { breadcrumb: 'Requests' },
  },
  {
    path: 'requests/:id',
    component: RequestDetailsComponent,
    canActivate: [authGuard, requestsGuard],
    canDeactivate: [UnsavedChangesGuard],
    data: {
      breadcrumbPath: [
        { label: 'Requests', url: '/requests' },
        { label: ':requestId', url: '' },
      ],
    },
  },
  {
    path: 'clients-matters',
    component: ClientsMattersComponent,
    canActivate: [authGuard, clientMattersGuard],
    data: { breadcrumb: 'Clients & Matters' },
  },
  {
    path: 'matters/:id',
    component: MatterDetailsComponent,
    canActivate: [authGuard, clientMattersGuard],
    data: {
      breadcrumbPath: [
        { label: 'Clients & Matters', url: '/clients-matters' },
        { label: ':matterId', url: '' },
      ],
    },
  },
  {
    path: 'admin',
    component: AdminComponent,
    canActivate: [authGuard],
    children: [
      { path: '', redirectTo: 'users', pathMatch: 'full' },
      {
        path: 'users',
        component: UsersComponent,
        canActivate: [usersManagementGuard],
        data: { breadcrumb: 'Resource Management' },
      },
      {
        path: 'practice-areas',
        component: PracticeAreasComponent,
        canActivate: [practiceAreasManagementGuard],
      },
      {
        path: 'practice-areas/:id',
        component: PracticeAreaDetailsComponent,
        canActivate: [practiceAreasManagementGuard],
        data: {
          breadcrumbPath: [
            { label: 'Practice Areas', url: '/admin/practice-areas' },
            { label: ':practiceAreaId', url: '' },
          ],
        },
      },
      {
        path: 'offices',
        component: OfficesComponent,
      },
      {
        path: 'offices/:officeCode',
        component: OfficeDetailsComponent,
        canDeactivate: [UnsavedChangesGuard],
        data: {
          breadcrumbPath: [
            { label: 'Offices', url: '/admin/offices' },
            { label: ':officeCode', url: '' },
          ],
        },
      },
      {
        path: 'roles',
        component: RolesComponent,
        canActivate: [permissionsManagementGuard],
        data: { breadcrumb: 'Roles' },
      },
      {
        path: 'audit-history',
        component: AuditHistoryComponent,
        canActivate: [auditHistoryGuard],
        data: { breadcrumb: 'Audit History' },
      },
      {
        path: 'feature-flags',
        component: FeaturesComponent,
        canActivate: [superAdminGuard],
        data: { breadcrumb: 'Feature Flags' },
      },
      {
        path: 'app-settings',
        component: AppSettingsComponent,
        canActivate: [appSettingsManagementGuard],
        data: { breadcrumb: 'App Settings' },
      },
      {
        path: 'user-feedback',
        component: UserFeedbackComponent,
        canActivate: [userFeedbackGuard],
        data: { breadcrumb: 'User Feedback' },
      },
      {
        path: 'logs',
        component: LogsComponent,
        canActivate: [logsGuard],
        data: { breadcrumb: 'Logs' },
      },
    ],
  },
  {
    // canDeactivate because the My Time Management tab hosts the editable capacity and
    // time-off panels, same as user-profile and resource-details.
    path: 'dashboard',
    component: DashboardComponent,
    canActivate: [authGuard],
    canDeactivate: [UnsavedChangesGuard],
    data: { breadcrumb: 'Dashboard' },
  },
  { path: '**', redirectTo: 'dashboard' },
];
